import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircleFill, XCircle, Search } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import AdminLayout, { isStaff } from '../AdminLayout';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import { currentLanguage } from '../../../common/ui/notify';
import { display, formatPrice, formatDate } from '../../../common/format';
import './AdminAuction.css';

const L = { geo: 0, en: 1, ru: 2, am: 3 };
const t = (geo, en, ru, am) => [geo, en, ru, am][L[currentLanguage()] ?? 0];

const KYC_TONE = { approved: 'cx-badge-sold', pending: 'cx-badge-new', rejected: 'cx-badge-danger', none: 'cx-badge-neutral' };
const YesNo = ({ ok }) => (ok
    ? <span className="admin-yes"><CheckCircleFill /></span>
    : <span className="admin-no"><XCircle /></span>);

const AdminAuctionBidders = () => {
    const user = useSelector((s) => s.user);
    const [bidders, setBidders] = useState([]);
    const [state, setState] = useState('loading');
    const [q, setQ] = useState('');

    const load = useCallback(() => {
        setState('loading');
        axios.get('/api/auction/admin/bidders')
            .then((res) => { setBidders(res.data.bidders || []); setState('ready'); })
            .catch(() => setState('error'));
    }, []);

    useEffect(() => { if (isStaff(user)) load(); }, [load, user]);

    const query = q.trim().toLowerCase();
    const list = query
        ? bidders.filter((b) => `${b.name || ''} ${b.surname || ''} ${b.email || ''} ${b.phone || ''} ${b.personal_id || ''} ${b.id}`.toLowerCase().includes(query))
        : bidders;

    const body = () => {
        if (state === 'loading') return <LoadingBlock label={t('იტვირთება…', 'Loading…', 'Загрузка…', 'Բեռնվում է…')} />;
        if (state === 'error') return <ErrorBlock title={t('შეცდომა', 'Error', 'Ошибка', 'Սխալ')} message={t('ბიდერების სია ვერ ჩაიტვირთა', 'Could not load bidders', 'Не удалось загрузить', 'Չհաջողվեց բեռնել')} />;
        if (!bidders.length) return <EmptyBlock title={t('ბიდერი არ არის', 'No bidders', 'Нет бидеров', 'Բիդերներ չկան')} />;

        return (
            <>
                <div className="admin-auction-form-row" style={{ marginBottom: 14 }}>
                    <div className="admin-carpicker-search" style={{ flex: '1 1 320px' }}>
                        <Search />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ძებნა სახელით, ელფოსტით, ნომრით…', 'Search name, email, phone…', 'Поиск…', 'Որոնել…')} />
                    </div>
                    <span className="admin-table-secondary">{t('სულ', 'Total', 'Всего', 'Ընդամենը')}: {bidders.length}</span>
                </div>

                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('ბიდერი', 'Bidder', 'Бидер', 'Բիդեր')}</th>
                                <th>{t('ტელეფონი', 'Phone', 'Телефон', 'Հեռախոս')}</th>
                                <th className="admin-hide-sm">{t('პირადი №', 'Personal №', 'Личный №', 'Անձնական №')}</th>
                                <th className="admin-hide-sm">{t('ქალაქი', 'City', 'Город', 'Քաղաք')}</th>
                                <th>KYC</th>
                                <th>{t('თანხმობა', 'Terms', 'Условия', 'Պայմաններ')}</th>
                                <th>{t('ბალანსი', 'Balance', 'Баланс', 'Մնացորդ')}</th>
                                <th className="admin-hide-sm">{t('რეგისტრაცია', 'Joined', 'Регистрация', 'Գրանցում')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((b) => (
                                <tr key={b.id}>
                                    <td>{b.id}</td>
                                    <td>
                                        <span className="admin-table-primary">{[b.name, b.surname].filter(Boolean).join(' ') || `#${b.id}`}{b.account_type === 'company' && b.company_name ? ` · ${b.company_name}` : ''}</span>
                                        <span className="admin-table-secondary">{display(b.email)}</span>
                                    </td>
                                    <td>
                                        <span className="admin-phone-cell">{display(b.phone)} {b.phone_verified ? <YesNo ok /> : <YesNo ok={false} />}</span>
                                    </td>
                                    <td className="admin-hide-sm">{display(b.personal_id)}</td>
                                    <td className="admin-hide-sm">{display(b.city)}</td>
                                    <td><span className={`cx-badge ${KYC_TONE[b.kyc_status] || 'cx-badge-neutral'}`}>{b.kyc_status || 'none'}</span></td>
                                    <td><YesNo ok={b.agreement_accepted} /></td>
                                    <td><strong>{formatPrice(b.deposit_available, 'GEL')}</strong></td>
                                    <td className="admin-hide-sm admin-table-secondary">{b.created_at ? formatDate(b.created_at) : '—'}</td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={9} className="admin-table-secondary">{t('ბიდერი ვერ მოიძებნა', 'No match', 'Не найдено', 'Չգտնվեց')}</td></tr>}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    return (
        <AdminLayout title={t('ბიდერები', 'Bidders', 'Бидеры', 'Բիդերներ')}
            subtitle={t('დარეგისტრირებული ბიდერების სია', 'Registered bidders', 'Зарегистрированные бидеры', 'Գրանցված բիդերներ')}>
            {body()}
        </AdminLayout>
    );
};

export default AdminAuctionBidders;
